import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Check } from 'lucide-react';

interface StatusDropdownProps {
  currentStatus: string;
  onStatusChange: (newStatus: string) => Promise<void>;
  campaignType: string;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  currentStatus,
  onStatusChange,
  campaignType
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const normalizedStatus = currentStatus.toLowerCase();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 120;
      const spaceBelow = window.innerHeight - buttonRect.bottom;

      setOpenUpward(spaceBelow < dropdownHeight);
    }
  }, [isOpen]);

  const handleStatusClick = async (newStatus: string) => {
    if (newStatus === normalizedStatus || isUpdating) return;

    setIsUpdating(true);
    try {
      await onStatusChange(newStatus);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const statusConfig = {
    active: {
      bgClass: 'bg-gradient-to-br from-[#05E0D1]/10 to-[#1875FF]/10',
      textClass: 'text-[#05E0D1]',
      borderClass: 'border-[#05E0D1]/30',
      hoverClass: 'hover:from-[#05E0D1]/20 hover:to-[#1875FF]/20',
      icon: Play,
      label: 'Active'
    },
    paused: {
      bgClass: 'bg-gradient-to-br from-gray-100 to-gray-200',
      textClass: 'text-gray-600',
      borderClass: 'border-gray-300',
      hoverClass: 'hover:from-gray-200 hover:to-gray-300',
      icon: Pause,
      label: 'Paused'
    }
  };

  const currentConfig = statusConfig[normalizedStatus as keyof typeof statusConfig] || statusConfig.paused;
  const CurrentIcon = currentConfig.icon;

  return (
    <div ref={dropdownRef} className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={() => !isUpdating && setIsOpen(!isOpen)}
        disabled={isUpdating}
        className={`
          inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border
          transition-all duration-300 cursor-pointer group
          ${currentConfig.bgClass} ${currentConfig.textClass} ${currentConfig.borderClass}
          ${!isUpdating && currentConfig.hoverClass}
          ${isUpdating ? 'opacity-50 cursor-wait' : 'hover:shadow-lg hover:scale-105'}
        `}
        title="Click to change status"
      >
        {isUpdating ? (
          <div className="mr-1.5 w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          <CurrentIcon size={12} className="mr-1.5 transition-transform group-hover:scale-110" />
        )}
        {currentConfig.label}
      </button>

      {isOpen && !isUpdating && (
        <div className={`absolute z-50 w-36 bg-white border-2 border-[#1875FF]/20 rounded-xl shadow-2xl shadow-[#1875FF]/20 overflow-hidden animate-fadeIn ${
          openUpward ? 'bottom-full mb-2' : 'mt-2'
        }`}>
          <div className="py-1">
            <button
              onClick={() => handleStatusClick('active')}
              className={`
                w-full px-4 py-2.5 text-left flex items-center justify-between
                transition-all duration-200 text-sm font-medium
                ${normalizedStatus === 'active'
                  ? 'bg-gradient-to-br from-[#05E0D1]/10 to-[#1875FF]/10 text-[#05E0D1]'
                  : 'text-gray-700 hover:bg-gradient-to-br hover:from-[#05E0D1]/5 hover:to-[#1875FF]/5'
                }
              `}
            >
              <div className="flex items-center">
                <Play size={14} className="mr-2" />
                Active
              </div>
              {normalizedStatus === 'active' && (
                <Check size={14} className="text-[#05E0D1]" />
              )}
            </button>

            <button
              onClick={() => handleStatusClick('paused')}
              className={`
                w-full px-4 py-2.5 text-left flex items-center justify-between
                transition-all duration-200 text-sm font-medium border-t border-gray-100
                ${normalizedStatus === 'paused'
                  ? 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700'
                  : 'text-gray-700 hover:bg-gradient-to-br hover:from-gray-50 hover:to-gray-100'
                }
              `}
            >
              <div className="flex items-center">
                <Pause size={14} className="mr-2" />
                Paused
              </div>
              {normalizedStatus === 'paused' && (
                <Check size={14} className="text-gray-600" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusDropdown;
